import { Routes } from '@angular/router';
import { authGuard } from './core/auth/guards/auth.guard';
import { hqOnlyGuard } from './core/auth/guards/hq-only.guard';
import { environment } from '../environments/environment';

/**
 * The kit gallery — every primitive, both languages, plus the shell with mock
 * menu data. It is the running counterpart of mockup frame S2 and the thing a
 * reviewer opens to check a component against the approved sheet.
 *
 * DEV ONLY, and unguarded on purpose: it renders no real data and issues no
 * request, so an auth guard would only make it unopenable. It is registered
 * from `!environment.production`, so a production bundle does not contain the
 * route OR (because the component is lazy) its chunk.
 */
const galleryRoutes: Routes = environment.production
  ? []
  : [
      {
        path: 'ui-gallery',
        loadComponent: () =>
          import('./ui-gallery/ui-gallery.component').then((m) => m.UiGalleryComponent),
      },
      {
        // The two bookings pages sit behind the auth guard, so they cannot be
        // screenshotted from a static build. This renders the REAL `bookings`
        // and `booking-details` components over an in-memory BookingsService,
        // which is what a reviewer compares against frames V1/V1m/V2/V2m.
        // Registered BEFORE `ui-gallery/:section`, which would otherwise match.
        path: 'ui-gallery/bookings',
        loadComponent: () =>
          import('./ui-gallery/bookings/gallery-bookings.component').then(
            (m) => m.GalleryBookingsComponent,
          ),
      },
      {
        // WI-V-03. The walk-in payment-link card sits at the BOTTOM of
        // `ui-gallery/bookings`, and `npm run shots` captures the viewport, not
        // the full page — so the card never reached a screenshot there. This
        // route is the same specimen with nothing above it. BEFORE
        // `ui-gallery/:section`, which would otherwise match.
        path: 'ui-gallery/walk-in-detail',
        loadComponent: () =>
          import('./ui-gallery/bookings/gallery-walk-in-detail.component').then(
            (m) => m.GalleryWalkInDetailPageComponent,
          ),
      },
      {
        // WI-V-05. The financial page sits behind `authGuard` and issues two
        // authenticated requests, so this is the only way its walk-in tiles and
        // Source column reach a static build. Same pattern as the specimens
        // above; BEFORE `ui-gallery/:section`, which would otherwise match.
        path: 'ui-gallery/financial',
        loadComponent: () =>
          import('./ui-gallery/financial/gallery-financial.component').then(
            (m) => m.GalleryFinancialComponent,
          ),
      },
      {
        // VENDOR-1. The dashboard is the post-login landing, behind `authGuard`,
        // and it issues six authenticated requests — so this is the only way its
        // tiles, rank lists and two tables reach a static build. Same pattern as
        // the specimens above; BEFORE `ui-gallery/:section`, which would
        // otherwise match. `/branch` is the SAME specimen as a branch session:
        // no branch picker, no per-branch table.
        path: 'ui-gallery/dashboard',
        loadComponent: () =>
          import('./ui-gallery/dashboard/gallery-dashboard.component').then(
            (m) => m.GalleryDashboardComponent,
          ),
      },
      {
        path: 'ui-gallery/dashboard/branch',
        loadComponent: () =>
          import('./ui-gallery/dashboard/gallery-dashboard.component').then(
            (m) => m.GalleryDashboardBranchComponent,
          ),
      },
      {
        // Waves V-W3 / V-W4 / V-W5, same pattern as `ui-gallery/bookings`:
        // the REAL page components over in-memory services provided in the
        // specimen's element injector. Registered BEFORE `ui-gallery/:section`,
        // which would otherwise match.
        path: 'ui-gallery/cars',
        loadComponent: () =>
          import('./ui-gallery/w345/gallery-cars.component').then(
            (m) => m.GalleryCarsComponent,
          ),
      },
      {
        // WI-V-02. The walk-in form sits behind `authGuard` and issues three
        // authenticated requests, so this is the only way it reaches a static
        // build. Same pattern as the specimens above; BEFORE `ui-gallery/:section`.
        path: 'ui-gallery/walk-in',
        loadComponent: () =>
          import('./ui-gallery/walk-in/gallery-walk-in.component').then(
            (m) => m.GalleryWalkInComponent,
          ),
      },
      {
        path: 'ui-gallery/car-form',
        loadComponent: () =>
          import('./ui-gallery/w345/gallery-car-form.component').then(
            (m) => m.GalleryCarFormComponent,
          ),
      },
      {
        path: 'ui-gallery/money',
        loadComponent: () =>
          import('./ui-gallery/w345/gallery-money.component').then(
            (m) => m.GalleryMoneyComponent,
          ),
      },
      {
        path: 'ui-gallery/catalogue',
        loadComponent: () =>
          import('./ui-gallery/w345/gallery-catalogue.component').then(
            (m) => m.GalleryCatalogueComponent,
          ),
      },
      {
        // The list-filters wave gave Reviews a filter bar built from nothing,
        // and the page issues three authenticated requests. Same pattern as
        // the specimens above; BEFORE `ui-gallery/:section`.
        path: 'ui-gallery/reviews',
        loadComponent: () =>
          import('./ui-gallery/w345/gallery-reviews.component').then(
            (m) => m.GalleryReviewsComponent,
          ),
      },
      {
        path: 'ui-gallery/branches',
        loadComponent: () =>
          import('./ui-gallery/w345/gallery-branches.component').then(
            (m) => m.GalleryBranchesComponent,
          ),
      },
      {
        path: 'ui-gallery/coupons',
        loadComponent: () => import('./ui-gallery/list-filters/gallery-campaigns.component').then(m => m.GalleryCouponsComponent),
      },
      {
        path: 'ui-gallery/promotions',
        loadComponent: () => import('./ui-gallery/list-filters/gallery-campaigns.component').then(m => m.GalleryPromotionsComponent),
      },
      {
        path: 'ui-gallery/insurance',
        loadComponent: () => import('./ui-gallery/list-filters/gallery-addons.component').then(m => m.GalleryInsuranceComponent),
      },
      {
        path: 'ui-gallery/addons',
        loadComponent: () => import('./ui-gallery/list-filters/gallery-addons.component').then(m => m.GalleryAddonsComponent),
      },
      {
        // Same component. The `:section` variant exists so the specimen's mock
        // sidebar can show BOTH the active and the resting entry state — with
        // every mock item pointing at one URL, `routerLinkActive` lit all nine.
        path: 'ui-gallery/:section',
        loadComponent: () =>
          import('./ui-gallery/ui-gallery.component').then((m) => m.UiGalleryComponent),
      },
    ];

export const appRoutes: Routes = [
  ...galleryRoutes,
  {
    path: 'auth',
    loadChildren: () =>
      import('./core/auth/auth.routes').then((m) => m.AuthRoutes),
  },
  {
    path: '',
    loadComponent: () =>
      import('./core/nav/nav.component').then((m) => m.NavComponent),
    canActivate: [authGuard],
    children: [
      {
        // VENDOR-1 (owner decision 2): the dashboard is the post-login landing.
        // `/bookings` stays a normal route, reachable from the sidebar, from
        // every tile that links into it and from every deep link and FCM tap.
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        data: { breadcrumb: 'NAV.DASHBOARD' },
        loadChildren: () =>
          import('./pages/dashboard/dashboard.routes').then(
            (m) => m.DASHBOARD_ROUTES
          ),
      },
      {
        path: 'bookings',
        data: { breadcrumb: 'NAV.BOOKINGS' },
        loadChildren: () =>
          import('./pages/bookings/bookings.routes').then((m) => m.BookingsRoutes),
      },
      {
        // PLAN-OPS-PAGES OP-V-02: one component, two views. `data.view` is what
        // the page sends as `view` to `GET /bookings`; a branch session gets
        // the same page scoped to itself, so there is no `hqOnlyGuard`.
        path: 'pickups',
        canActivate: [authGuard],
        data: { breadcrumb: 'NAV.PICKUPS', view: 'pickups' },
        loadComponent: () =>
          import('./pages/ops/ops-bookings.component').then(
            (m) => m.OpsBookingsComponent
          ),
      },
      {
        path: 'returns',
        canActivate: [authGuard],
        data: { breadcrumb: 'NAV.RETURNS', view: 'returns' },
        loadComponent: () =>
          import('./pages/ops/ops-bookings.component').then(
            (m) => m.OpsBookingsComponent
          ),
      },
      {
        // BR-59: same reasoning — /offices/settings is the HQ's own company
        // profile, not a leaf's to edit. The nav hides it for a branch session.
        path: 'offices',
        data: { breadcrumb: 'NAV.OFFICES' },
        canActivate: [hqOnlyGuard],
        loadChildren: () =>
          import('./pages/offices/offices.routes').then((m) => m.OfficesRoutes),
      },
      {
        path: 'cars',
        data: { breadcrumb: 'NAV.CARS' },
        loadChildren: () =>
          import('./pages/cars/cars.routes').then((m) => m.CarsRoutes),
      },
      {
        path: 'car-types',
        data: { breadcrumb: 'NAV.CAR_TYPES' },
        loadChildren: () =>
          import('./pages/car-types/car-types.routes').then(
            (m) => m.CarTypesRoutes
          ),
      },
      {
        path: 'companies',
        data: { breadcrumb: 'NAV.COMPANIES' },
        loadChildren: () =>
          import('./pages/companies/companies.routes').then(
            (m) => m.CompaniesRoutes
          ),
      },
      {
        path: 'car-brands',
        data: { breadcrumb: 'NAV.CAR_BRANDS' },
        loadChildren: () =>
          import('./pages/car-brands/car-brands.routes').then(
            (m) => m.CarBrandsRoutes
          ),
      },
      {
        path: 'additional-services',
        data: { breadcrumb: 'NAV.ADDITIONAL_SERVICES' },
        loadChildren: () =>
          import('./pages/additional-services/additional-services.routes').then(
            (m) => m.AdditionalServicesRoutes
          ),
      },
      {
        path: 'insurance-types',
        data: { breadcrumb: 'NAV.INSURANCE_TYPES' },
        loadChildren: () =>
          import('./pages/insurance-types/insurance-types.routes').then(
            (m) => m.InsuranceTypesRoutes
          ),
      },
      {
        // MarTech Phase 1. `authGuard` only, deliberately no `hqOnlyGuard`: a
        // branch session may READ its company's coupons and promotions — the
        // API is read-only for a `branch` token — and the pages hide every
        // write behind `isBranchUser`, the same rule the catalogue uses.
        path: 'coupons',
        data: { breadcrumb: 'NAV.COUPONS' },
        canActivate: [authGuard],
        loadChildren: () =>
          import('./pages/coupons/coupons.routes').then((m) => m.CouponsRoutes),
      },
      {
        path: 'promotions',
        data: { breadcrumb: 'NAV.PROMOTIONS' },
        canActivate: [authGuard],
        loadChildren: () =>
          import('./pages/promotions/promotions.routes').then(
            (m) => m.PromotionsRoutes
          ),
      },
      {
        path: 'reviews',
        data: { breadcrumb: 'NAV.REVIEWS' },
        loadChildren: () =>
          import('./pages/reviews/reviews.routes').then((m) => m.reviewsRoutes),
      },
      {
        path: 'financial',
        data: { breadcrumb: 'NAV.FINANCIAL' },
        loadChildren: () =>
          import('./pages/financial/financial.routes').then(
            (m) => m.FINANCIAL_ROUTES
          ),
      },
      {
        // BR-59: HQ only — a branch session could otherwise open branch
        // management by typing the URL; the nav merely hides the entry.
        path: 'branches',
        data: { breadcrumb: 'NAV.BRANCHES' },
        canActivate: [hqOnlyGuard],
        loadChildren: () =>
          import('./pages/branches/branches.routes').then(
            (m) => m.BranchesRoutes
          ),
      },
      {
        // Vendor Users (P1). HQ only, same reasoning as `/branches`: every
        // `/vendor-users` route is `@Roles('admin','office')` and a branch
        // token is refused by the roles guard, so a leaf typing the URL would
        // get a page of 403s instead of a redirect.
        path: 'users',
        data: { breadcrumb: 'NAV.USERS' },
        canActivate: [hqOnlyGuard],
        loadChildren: () =>
          import('./pages/users/users.routes').then((m) => m.UsersRoutes),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/dashboard',
  },
];
